import PropTypes from "prop-types";
import React from "react";

import { staticURL } from "lib/img";
import { CHECKIN_TYPE } from "lib/constants";
import { TranslationFn } from "lib/types";
import FeatureFlag from "workstep/components/layout/FeatureFlag";
import HubNote from "employee/HubNote";
import ProfileIconButton from "employee/ProfileIconButton";
import SummaryHubLink from "employee/SummaryHubLink";

type TContext = { t: TranslationFn };

const AlwaysOnSummaryText = (_props: Record<string, never>, { t: _ }: TContext) => (
    <p>{_("Thank you for your feedback. Feel free to provide more any time!")}</p>
);

AlwaysOnSummaryText.contextTypes = {
    t: PropTypes.func,
};

type DefaultCheckinSummaryTextProps = {
    companyName?: string;
};

const DefaultCheckinSummaryText = (
    { companyName = "" }: DefaultCheckinSummaryTextProps,
    { t: _ }: TContext,
) => (
    <>
        <p>
            {_("Your feedback helps us make {company} a better company!", {
                company: companyName,
            })}
        </p>
        <p>
            {_(
                "We understand that your thoughts will change over time, which is why we would appreciate your ongoing participation in future surveys.",
            )}
        </p>
    </>
);

DefaultCheckinSummaryText.contextTypes = {
    t: PropTypes.func,
};

type CustomCheckinSummaryTextProps = {
    thankYouMessage: string;
};

const CustomCheckinSummaryText = (
    { thankYouMessage }: CustomCheckinSummaryTextProps,
    { t: _ }: TContext,
) => (
    <div
        // eslint-disable-next-line react/no-danger
        dangerouslySetInnerHTML={{
            __html: _(thankYouMessage),
        }}
    />
);

CustomCheckinSummaryText.contextTypes = {
    t: PropTypes.func,
};

type CheckinThankYouTitleProps = {
    checkinThankYouTitle?: string | null;
};

function CheckinThankYouTitle(
    { checkinThankYouTitle = null }: CheckinThankYouTitleProps,
    { t: _ }: TContext,
) {
    // eslint-disable-next-line react/jsx-no-useless-fragment
    return <>{checkinThankYouTitle || _("Thanks for your feedback!")}</>;
}

CheckinThankYouTitle.contextTypes = {
    t: PropTypes.func,
};

type CheckinThankYouMessageProps = {
    checkinThankYouMessage?: string | null;
    checkinType?: number | null;
    companyName?: string | null;
};

export function CheckinThankYouMessage({
    checkinThankYouMessage = null,
    checkinType = null,
    companyName = null,
}: CheckinThankYouMessageProps) {
    if (checkinThankYouMessage) {
        return <CustomCheckinSummaryText thankYouMessage={checkinThankYouMessage} />;
    }
    if (checkinType === CHECKIN_TYPE.ALWAYS_ON) {
        return <AlwaysOnSummaryText />;
    }
    return <DefaultCheckinSummaryText companyName={companyName ?? ""} />;
}

type CandidateCheckInSummaryProps = {
    companyName: string;
    checkinType: number;
    checkinThankYouTitle?: string | null;
    checkinThankYouMessage?: string | null;
};

export default function CandidateCheckInSummary(
    {
        companyName,
        checkinType,
        checkinThankYouTitle = null,
        checkinThankYouMessage = null,
    }: CandidateCheckInSummaryProps,
    { t: _ }: TContext,
) {
    return (
        <div className="check-in__landing-page">
            <ProfileIconButton />
            <div className="check-in__landing-page-image-container">
                <img
                    src={staticURL("thank-you.gif")}
                    className="thank-you"
                    alt={_("Thanks for your feedback!")}
                />
            </div>
            <div className="check-in__landing-page-content">
                <h2 className="check-in__landing-page-title" data-test="check-in-thank-you-title">
                    <CheckinThankYouTitle checkinThankYouTitle={checkinThankYouTitle} />
                </h2>
                <CheckinThankYouMessage
                    checkinType={checkinType}
                    checkinThankYouMessage={checkinThankYouMessage}
                    companyName={companyName}
                />
                <FeatureFlag featureName="hub-2-0">
                    <div style={{ maxWidth: 600, margin: "auto" }}>
                        <HubNote fallback={<SummaryHubLink />} hideDismiss />
                    </div>
                </FeatureFlag>
            </div>
        </div>
    );
}

CandidateCheckInSummary.contextTypes = {
    t: PropTypes.func,
};
